import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuArrowUpDown, LuChevronDown } from "react-icons/lu";
import { SortBy, SortOrder } from "../../../api/projectsApi";
import {
  useProjectsListActions,
  useProjectsListSortBy,
  useProjectsListSortOrder,
} from "../../../core_stores/useProjectsListStore";
import { colors, typography, effects, layout } from "../../../design/tokens";
import { useIsSideMenuOpen } from "../../../view_stores/useSideMenuStore";

interface SortOption {
  label: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
}

const SORT_OPTIONS: SortOption[] = [
  { label: "Recently opened", sortBy: "last_access", sortOrder: "desc" },
  { label: "Oldest opened", sortBy: "last_access", sortOrder: "asc" },
  { label: "Recently created", sortBy: "created_at", sortOrder: "desc" },
  { label: "Oldest created", sortBy: "created_at", sortOrder: "asc" },
  { label: "Name (A → Z)", sortBy: "project_name", sortOrder: "asc" },
  { label: "Name (Z → A)", sortBy: "project_name", sortOrder: "desc" },
];

const DEFAULT_LABEL = "Recently opened";

export default function SideMenuSortDropdown(): JSX.Element {
  const sortBy = useProjectsListSortBy();
  const sortOrder = useProjectsListSortOrder();
  const { setSortOptions } = useProjectsListActions();
  const isSideMenuOpen = useIsSideMenuOpen();

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = SORT_OPTIONS.findIndex(
    (o) => o.sortBy === sortBy && o.sortOrder === sortOrder
  );
  const selectedLabel = selectedIndex >= 0 ? SORT_OPTIONS[selectedIndex].label : DEFAULT_LABEL;

  function scrollToIndex(index: number): void {
    optionRefs.current[index]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }

  useEffect(() => {
    if (!isSideMenuOpen) {
      setIsOpen(false);
    }
  }, [isSideMenuOpen]);

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200),
    });
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target as Node);
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target as Node);

      if (clickedOutsideDropdown && clickedOutsideButton) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setFocusedIndex(isOpen ? selectedIndex : -1);
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent): void {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1 >= SORT_OPTIONS.length ? 0 : prev + 1;
            scrollToIndex(next);
            return next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1 < 0 ? SORT_OPTIONS.length - 1 : prev - 1;
            scrollToIndex(next);
            return next;
          });
          break;
        case "Enter":
          if (focusedIndex !== -1) {
            e.preventDefault();
            const option = SORT_OPTIONS[focusedIndex];
            setSortOptions(option.sortBy, option.sortOrder);
            setIsOpen(false);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, focusedIndex, setSortOptions]);

  function handleSelect(option: SortOption): void {
    setSortOptions(option.sortBy, option.sortOrder);
    setIsOpen(false);
  }

  function handleToggle(): void {
    setIsOpen(!isOpen);
  }

  const chevronRotation = isOpen ? "rotate-180" : "";

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`flex items-center gap-2 px-4 py-2 w-full
          ${colors.surface.secondary} border ${colors.border.primary} ${effects.borderRadius.full}
          ${typography.size.sm} ${colors.text.primary}
          ${effects.transition.default} hover:border-white/30 focus:border-primary-blue focus:outline-none`}
      >
        <LuArrowUpDown size={20} className={`flex-shrink-0 ${colors.gray[400]}`} />
        <span className="flex-1 text-left truncate">{selectedLabel}</span>
        <LuChevronDown
          size={16}
          className={`${effects.transition.transform} flex-shrink-0 ${colors.gray[400]} ${chevronRotation}`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
            }}
            className={`${layout.zIndex.modal} ${colors.surface.secondary} border ${colors.border.primary}
              ${effects.borderRadius.xl} ${effects.shadow.custom.modal} max-h-[300px] overflow-y-auto`}
          >
            {SORT_OPTIONS.map((option, index) => {
              const isSelected = option.sortBy === sortBy && option.sortOrder === sortOrder;
              const isFocused = focusedIndex === index;

              const baseClass = `w-full text-left px-4 py-2.5 ${typography.size.sm} ${effects.transition.default}`;
              const selectedClass = isSelected ? "bg-primary-blue/20 text-white" : `${colors.text.primary} hover:bg-white/10`;
              const focusedClass = isFocused ? "ring-1 ring-primary-blue bg-white/5" : "";

              return (
                <button
                  key={`${option.sortBy}-${option.sortOrder}`}
                  ref={(el) => (optionRefs.current[index] = el)}
                  onClick={() => handleSelect(option)}
                  className={`${baseClass} ${selectedClass} ${focusedClass}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
